import * as React from 'react';
import { cn } from '../../lib/cn';

/**
 * RadioCardGroup — a short list of choices that each need a sentence to be understood (a role
 * and what it may do), all visible at once so they can be compared before choosing. Use it where
 * there is room (a dialog, a form); inline at the end of a row use `Select` with item
 * descriptions instead.
 *
 * Native radios in a `<fieldset>`: arrow-key movement, form semantics and the group's name (its
 * `<legend>`) come from the platform. Each card is the radio's `<label>`, so the whole card is
 * the hit target; the radio is NAMED by the option's label alone (`aria-labelledby`) and
 * DESCRIBED by its description, so a screen reader does not read the sentence as the name.
 */
export interface RadioCardOption {
  value: string;
  label: React.ReactNode;
  description?: React.ReactNode;
  disabled?: boolean;
}

export interface RadioCardGroupProps {
  legend: React.ReactNode;
  /** The radios' shared `name` — unique on the page. */
  name: string;
  value: string;
  onValueChange: (value: string) => void;
  options: readonly RadioCardOption[];
  className?: string;
}

export function RadioCardGroup({ legend, name, value, onValueChange, options, className }: RadioCardGroupProps) {
  const id = React.useId();
  return (
    <fieldset className={cn('tw:m-0 tw:flex tw:min-w-0 tw:flex-col tw:gap-2 tw:border-0 tw:p-0', className)}>
      <legend className="tw:mb-2 tw:p-0 tw:font-sans tw:text-sm tw:font-medium tw:text-foreground">{legend}</legend>
      {options.map((option) => {
        const labelId = `${id}-${option.value}-label`;
        const descriptionId = `${id}-${option.value}-description`;
        return (
          <label
            key={option.value}
            className={cn(
              'tw:flex tw:cursor-pointer tw:items-start tw:gap-3 tw:rounded-lg tw:border tw:border-border tw:bg-card tw:p-3 tw:font-sans tw:transition-colors',
              'tw:hover:bg-accent tw:has-checked:border-foreground tw:has-checked:bg-foreground/[0.04]',
              'tw:has-focus-visible:ring-2 tw:has-focus-visible:ring-ring',
              'tw:has-disabled:cursor-not-allowed tw:has-disabled:opacity-50 tw:has-disabled:hover:bg-card'
            )}
          >
            <input
              type="radio"
              name={name}
              value={option.value}
              checked={value === option.value}
              disabled={option.disabled}
              onChange={() => onValueChange(option.value)}
              aria-labelledby={labelId}
              aria-describedby={option.description ? descriptionId : undefined}
              className="tw:mt-0.5 tw:size-4 tw:shrink-0 tw:accent-foreground tw:focus-visible:outline-none"
            />
            <span className="tw:flex tw:min-w-0 tw:flex-col tw:gap-0.5">
              <span id={labelId} className="tw:text-sm tw:font-medium tw:text-foreground">
                {option.label}
              </span>
              {option.description && (
                <span id={descriptionId} className="tw:text-sm tw:leading-snug tw:text-muted-foreground">
                  {option.description}
                </span>
              )}
            </span>
          </label>
        );
      })}
    </fieldset>
  );
}
